import React, { useMemo, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  FiActivity, FiBookOpen, FiBriefcase, FiChevronLeft, FiChevronRight, FiClipboard,
  FiCreditCard, FiFileText, FiGrid, FiLogOut, FiMessageSquare, FiSettings, FiShield, FiUsers
} from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';
import { signOut } from 'firebase/auth';
import { auth } from '../firebase';
import { useBranding } from '../contexts/BrandingContext';
import OrganisationMark from './OrganisationMark';

export default function Sidebar() {
  const { userData } = useAuth();
  const { branding } = useBranding();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const role = String(userData?.role || 'staff').toLowerCase();

  const items = useMemo(() => [
    { to: '/', label: 'Dashboard', icon: FiGrid },
    { to: '/assignments', label: 'Assignments', icon: FiClipboard },
    { to: '/quick-works', label: 'Quick Works', icon: FiActivity },
    { to: '/matters', label: 'Matter / Case Diary', icon: FiBookOpen },
    { to: '/staff-master', label: 'Staff Master', icon: FiUsers, roles: ['admin', 'partner'] },
    { to: '/clients', label: 'Clients', icon: FiBriefcase, roles: ['admin', 'partner', 'accounts'] },
    { to: '/billing', label: 'Billing Management', icon: FiCreditCard, roles: ['accounts', 'partner'] },
    ...(role === 'partner' && userData?.communicationCentreEnabled === true ? [{ to: '/communication-centre', label: 'Communication Centre', icon: FiMessageSquare }] : []),
    { to: '/dsc-management', label: 'DSC Management', icon: FiShield },
    { to: '/reports', label: 'Reports', icon: FiFileText },
    { to: '/attendance', label: 'Attendance', icon: FiActivity },
    { to: '/settings', label: 'Settings', icon: FiSettings, roles: ['admin'] },
  ].filter((item) => !item.roles || item.roles.includes(role)), [role, userData?.communicationCentreEnabled]);

  const handleLogout = async () => {
    await signOut(auth);
    navigate('/login');
  };

  return (
    <aside className={`app-sidebar ${collapsed ? 'is-collapsed' : ''}`}>
      <div className="sidebar-brand">
        <div className="sidebar-brand-lockup">
          <img src="/brand/pravah-symbol.png" alt="Pravah symbol" className="sidebar-brand-symbol" />
        </div>
        <button className="sidebar-toggle" type="button" onClick={() => setCollapsed((value) => !value)} aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}>{collapsed ? <FiChevronRight /> : <FiChevronLeft />}</button>
      </div>

      {!collapsed && (
        <div className="sidebar-organisation">
          {branding.showOrganisationLogo !== false && (
            <OrganisationMark
              branding={branding}
              className="h-9 w-9 rounded-lg bg-white object-contain p-1"
              fallbackClassName="grid h-9 w-9 place-items-center rounded-lg bg-white/10 text-sm font-bold text-[#F7F3E8]"
            />
          )}
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-[#F7F3E8]">{branding.organisationName}</p>
            <p className="truncate text-xs capitalize text-[#F7F3E8]/55">{userData?.role || 'staff'} workspace</p>
          </div>
        </div>
      )}

      <nav className="sidebar-nav">
        {items.map(({ to, label, icon: Icon }) => (
          <NavLink key={to} to={to} end={to === '/'} title={collapsed ? label : undefined} className={({ isActive }) => `sidebar-link ${isActive ? 'is-active' : ''}`}>
            <Icon className="sidebar-link-icon" />
            {!collapsed && <span>{label}</span>}
          </NavLink>
        ))}
      </nav>

      <div className="sidebar-footer">
        {!collapsed && <p className="mb-3 truncate text-xs text-[#F7F3E8]/45">Signed in as {userData?.name || 'User'}</p>}
        <button onClick={handleLogout} className="sidebar-logout" type="button"><FiLogOut />{!collapsed && <span>Sign out</span>}</button>
      </div>
    </aside>
  );
}
